/**
 * Login-time TOTP verify via Kong → auth-service.
 */

import { kongBrowserFetchUrl } from "@/lib/kongApi";
import { fetchWithTimeout, LOGIN_FETCH_TIMEOUT_MS } from "@/lib/fetchWithTimeout";

async function parseErrorMessage(res) {
  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }
  const d = data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && typeof d.message === "string") return d.message;
  if (typeof data?.message === "string") return data.message;
  return res.statusText || "Request failed";
}

export class TotpAuthClient {
  /**
   * @param {{ totp_session: string, code: string }} params
   * @returns {Promise<{
   *   status: string,
   *   token: string,
   *   email: string,
   *   tenant_id?: string,
   *   realm?: string,
   *   roles: string[],
   *   workspace_client_allowlist: string[],
   *   default_workspace_id?: string
   * }>}
   */
  async verify({ totp_session, code }) {
    const session = String(totp_session || "").trim();
    const otp = String(code || "").replace(/\s+/g, "");
    if (!session || !otp) {
      throw new Error("Enter the 6-digit authenticator code.");
    }
    const url = kongBrowserFetchUrl("/api/v1/auth/tenant-dashboard/totp/verify");
    const res = await fetchWithTimeout(
      url,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ totp_session: session, code: otp }),
      },
      LOGIN_FETCH_TIMEOUT_MS
    );
    if (!res.ok) {
      throw new Error(await parseErrorMessage(res));
    }
    const data = await res.json();
    const token = data.access_token || data.token;
    if (!token || typeof token !== "string") {
      throw new Error("Invalid verification response: missing access token.");
    }
    return {
      status: "ok",
      token,
      email: data.user?.email || "",
      tenant_id: data.tenant_id != null ? String(data.tenant_id) : "",
      realm: data.realm != null ? String(data.realm) : "",
      roles: Array.isArray(data.roles) ? data.roles.map(String) : [],
      workspace_client_allowlist: Array.isArray(data.workspace_client_allowlist)
        ? data.workspace_client_allowlist.map(String)
        : [],
      default_workspace_id:
        data.default_workspace_id != null ? String(data.default_workspace_id) : "",
    };
  }
}
